import React from 'react';

import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base/ajax-listing.jsx';
import { Dispatcher } from '$partials/listings';
import { Stream } from '$partials/modules';
import { InListingBlock } from '$partials/modules/blocks';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').SoldPropertyListingPage}
 */
const SoldPropertyListingPage = (props) => {
    const page = props.data.page;
    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        page: page.items?.pageInfo?.page || 1,
        path: `/api/v1/listings/${encodeURIComponent(props.data.uri)}`,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
    });

    const typenames = page.midListingModules.map((item) => item.__typename);
    const inListingExists = typenames.includes('InListingBlock');

    const inListingItems = page.midListingModules.filter((item) => item.__typename === 'InListingBlock');
    const keyLinksItems = page.midListingModules.filter((item) => item.__typename === 'KeyLinksBlock');
    const inListingOne = inListingItems[0];
    const inListingTwo = inListingItems[1];

    let firstBatchItems;
    let remainingItems;
    let remainingItemsAfterFirstInListing;
    let remainingItemsAfterSecondInListing;

    if(!inListingExists) {
        firstBatchItems = listing?.items?.slice?.(0, 4);
        remainingItems = listing?.items?.slice?.(4);
    } else if(inListingExists && inListingItems.length === 1) {
        firstBatchItems = listing?.items?.slice?.(0, 3);
        remainingItems = listing?.items?.slice?.(3);
    } else if(inListingExists && inListingItems.length === 2) {
        firstBatchItems = listing?.items?.slice?.(0, 3);
        remainingItems = listing?.items?.slice?.(3);
        remainingItemsAfterFirstInListing = listing?.items?.slice?.(3, 5);
        remainingItemsAfterSecondInListing = listing?.items?.slice?.(5);
    }

    return (
        <main className='container'>
            {/* Desktop */}
            <div className='hide md-show'>
                <div className='col-12 flex flex-wrap sm-flex-no-wrap'>
                    <div
                        className='flex-grow py6 md-py7 md-pr0 bg-sand flex items-center pl5 md-pl6 sm-border-right border-bottom-2px border-top grey'
                        style={{width: 'calc(50% + 20px)'}}>
                        <h2 className='type-style-2 black'>
                            {page.title}
                        </h2>
                    </div>
                </div>
            </div>

            {/* Tablet / Mobile */}
            <div className='md-hide'>
                <div className='col-12 flex flex-wrap sm-flex-no-wrap'>
                    <div
                        className='flex-grow py6 md-py7 md-pr0 bg-sand flex items-center pl5 md-pl6 border-right border-bottom-2px border-top grey'
                        style={{width: 'calc(50% + 20px)'}}>
                        <h2 className='type-style-2 black'>
                            {page.title}
                        </h2>
                    </div>
                </div>
            </div>

            <AjaxListingContext.Provider value={listing}>
                <div className='flex flex-wrap col-12 property-item-row-gap'>
                    {/* No items found */}
                    {listing.items.length === 0 && (
                        <div className='col-12 border-top border-bottom bg-sand grey py9 px5 md-px6'>
                            <h2 className='type-style-2 black'>
                                No Properties Found
                            </h2>
                        </div>
                    )}

                    {/* If no InListings, display first 4 items */}
                    {!inListingExists && firstBatchItems.map((item) => {
                        return (
                            <div
                                className={'col-12 sm-col-6 expand-interaction bg-sand property-item'}
                                key={item?.node?.id}>
                                <Dispatcher item={item?.node}/>
                            </div>
                        );
                    })}

                    {/* If InListing modules, display first 3 items then the first InListing */}
                    {inListingExists && (inListingItems.length === 1 || inListingItems.length === 2) && (
                        <>
                            {firstBatchItems.map((item) => {
                                return (
                                    <div
                                        className={'col-12 sm-col-6 expand-interaction bg-sand property-item'}
                                        key={item?.node?.id}>
                                        <Dispatcher item={item?.node}/>
                                    </div>
                                );
                            })}
                            {listing?.items?.length >= 3 && (
                                <div
                                    className={'hide sm-show col-12 sm-col-6 expand-interaction bg-sand property-item'}>
                                    <InListingBlock
                                        hubspot={props.hubspot}
                                        page={page}
                                        {...inListingOne}/>
                                </div>
                            )}
                        </>
                    )}

                    {/* If Key Links module */}
                    {keyLinksItems.length > 0 &&
                        <aside className='col-12'>
                            <Stream
                                modules={keyLinksItems}
                                namespace='property-listing' />
                        </aside>
                    }

                    {/* Remaining listing items if no InListing */}
                    {!inListingExists && remainingItems.map((item) => {
                        return (
                            <div
                                className={'col-12 sm-col-6 expand-interaction bg-sand property-item'}
                                key={item?.node?.id}>
                                <Dispatcher item={item?.node}/>
                            </div>
                        );
                    })}

                    {/* Remaining listing items if one InListing */}
                    {inListingExists && inListingItems.length === 1 && remainingItems.map((item) => {
                        return (
                            <div
                                className={'col-12 sm-col-6 expand-interaction bg-sand property-item'}
                                key={item?.node?.id}>
                                <Dispatcher item={item?.node}/>
                            </div>
                        );
                    })}

                    {/* Two InListings */}
                    {inListingExists && inListingItems.length === 2 && (
                        <>
                            {remainingItemsAfterFirstInListing.map((item) => {
                                return (
                                    <div
                                        className={'col-12 sm-col-6 expand-interaction bg-sand property-item'}
                                        key={item?.node?.id}>
                                        <Dispatcher item={item?.node}/>
                                    </div>
                                );
                            })}
                            {listing?.items?.length >= 6 && (
                                <div
                                    className={'hide sm-show col-12 sm-col-6 expand-interaction bg-sand property-item'}>
                                    <InListingBlock
                                        hubspot={props.hubspot}
                                        page={page}
                                        {...inListingTwo}/>
                                </div>
                            )}
                        </>
                    )}
                    {/* Remaining listing items if two InListings */}
                    {inListingExists && inListingItems.length === 2 && remainingItemsAfterSecondInListing?.map((item) => {
                        return (
                            <div
                                className={'col-12 sm-col-6 expand-interaction bg-sand property-item'}
                                key={item?.node?.id}>
                                <Dispatcher item={item?.node}/>
                            </div>
                        );
                    })}

                    {listing?.page < listing?.totalPages &&
                        <InfinitePagination>
                            <p className='type-style-5 px5 py7'>Loading</p>
                        </InfinitePagination>
                    }

                    {/* remaining modules */}
                    {page.modules &&
                        <div className='col-12'>
                            <Stream
                                modules={page.modules}
                                namespace='property-listing' />
                        </div>}

                </div>
            </AjaxListingContext.Provider>
        </main>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default SoldPropertyListingPage ;
